import React, { useEffect, useState } from 'react';
import { JOBS_LIVE, isOnHold, needsApproval, pageOf, refreshJobs, useJobs } from '../../services/jobs';
import { avatarColours, initials } from '../Calls/avatar';
import JobDetail from './JobDetail';
import '../Calls/Calls.css';
import './Jobs.css';

/**
 * Job Progress — every sold job (Zoho Jobs, created when a Deal goes Closed Won). Two pages:
 *   In Progress   Needs setup / approval (plan not approved yet) · Active (approved, work under way)
 *   Completed     Completed · Cancelled
 * Open a job to set up / approve its plan, tick steps, take payments, put it on hold or cancel it (JobDetail).
 */

const FILTERS = {
  progress: [
    { id: 'all', label: 'All', test: () => true },
    { id: 'setup', label: 'Needs setup / approval', test: needsApproval },
    { id: 'active', label: 'Active', test: (j) => !needsApproval(j) },
  ],
  completed: [
    { id: 'Completed', label: 'Completed', test: (j) => j.stage === 'Completed' },
    { id: 'Cancelled', label: 'Cancelled', test: (j) => j.stage === 'Cancelled' },
  ],
};

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-CA')}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '');

// the coloured chip for where the job is (same colours as Zoho)
export const stageChip = (j) => {
  if (j.stage === 'Completed') return { tone: 'job-done', text: 'Completed' };
  if (j.stage === 'Cancelled') return { tone: 'job-cancelled', text: 'Cancelled' };
  if (isOnHold(j)) return { tone: 'job-pending', text: j.step };
  if (j.planStatus === 'Pending Approval') return { tone: 'job-pending', text: 'Waiting for approval' };
  if (j.planStatus === 'Change Requested') return { tone: 'job-change', text: 'Change requested' };
  if (j.planStatus !== 'Approved') return { tone: 'job-setup', text: 'Needs setup' };
  return { tone: 'job-active', text: j.step && j.step !== 'In progress' ? j.step : 'In progress' };
};

const JobProgress = ({ page = 'progress', onOpenCustomer }) => {
  const { jobs, status, error } = useJobs();
  const [filter, setFilter] = useState(FILTERS[page][0].id);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    refreshJobs();
  }, []);

  if (openId) return <JobDetail id={openId} onBack={() => setOpenId(null)} onOpenCustomer={onOpenCustomer} />;

  const mine = jobs.filter((j) => pageOf(j) === page);
  const q = search.trim().toLowerCase();
  const inView = mine.filter((j) => !q || `${j.name} ${j.address} ${j.phone} ${j.agent}`.toLowerCase().includes(q));
  const count = (f) => inView.filter(f.test).length;
  const current = FILTERS[page].find((f) => f.id === filter) || FILTERS[page][0];
  const list = inView.filter(current.test);
  const loading = status === 'loading' && !jobs.length;

  return (
    <div className="calls">
      <div className="calls-head">
        <div className="calls-title-row">
          <h1>{page === 'completed' ? 'Completed Jobs' : 'Jobs in Progress'}</h1>
          {JOBS_LIVE && (
            <button type="button" className="calls-refresh" onClick={() => refreshJobs()} disabled={status === 'loading'}>
              {status === 'loading' ? '⟳ Loading…' : '↻'}
            </button>
          )}
        </div>
        {status === 'error' && <p className="calls-error">⚠ {error}</p>}

        <div className="calls-search">
          <span className="calls-search-icon" aria-hidden="true">⌕</span>
          <input type="search" placeholder="Search by name, address, phone or agent…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>

        <div className="calls-tabs">
          {FILTERS[page].map((f) => (
            <button key={f.id} type="button" className={`calls-tab${current.id === f.id ? ' active' : ''}`} onClick={() => setFilter(f.id)}>
              {f.label}
              <span className="calls-tab-count">{count(f)}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="calls-list">
        {!JOBS_LIVE && <p className="calls-empty">Jobs come from Zoho — not connected in test mode.</p>}
        {loading && <p className="calls-empty">Loading from Zoho…</p>}
        {JOBS_LIVE && !loading && list.length === 0 && <p className="calls-empty">No jobs here right now.</p>}
        {list.map((j) => {
          const [bg, fg] = avatarColours(j.name);
          const chip = stageChip(j);
          return (
            <button type="button" className="calls-card" key={j.id} onClick={() => setOpenId(j.id)}>
              <span className="calls-avatar" style={{ background: bg, color: fg }}>{initials(j.name)}</span>
              <div className="calls-card-main">
                <div className="calls-card-top">
                  <span className="calls-name">{j.name}</span>
                  {j.agent && <span className="calls-ago">{j.agent}</span>}
                </div>
                <div className="calls-address">{j.address}</div>
                <div className="calls-chips">
                  <span className={`calls-chip ${chip.tone}`}>{chip.text}</span>
                  {j.jobDay && pageOf(j) === 'progress' && <span className="calls-chip scheduled">📅 {day(j.jobDay)}</span>}
                  {j.total > 0 && (
                    <span className="calls-chip amount" title="Balance owing / invoice total">
                      {j.balance > 0 ? `${money(j.balance)} owing` : money(j.total)}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default JobProgress;
